import { useState } from 'react';
import { X, CreditCard, Truck, Shield, Check, Building2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useShop } from '@/context/ShopContext';
import { ShippingAddress } from '@/services/woocommerce';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired once the order is placed, so a host page can stop showing an
      empty-cart state — placing an order necessarily empties the cart. */
  onOrderComplete?: () => void;
}

export function CheckoutModal({ isOpen, onClose, onOrderComplete }: CheckoutModalProps) {
  const { cart, cartTotal, checkout, discount, appliedCoupon, shipping, orderTotal } = useShop();
  const [step, setStep] = useState<'shipping' | 'payment' | 'confirmation'>('shipping');
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderPaymentMethod, setOrderPaymentMethod] = useState('');
  const [checkoutError, setCheckoutError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'ZA',
    sameAsBilling: true,
    paymentMethod: 'bacs',   // EFT is default — always enabled
    customerNote: ''
  });

  const [shippingErrors, setShippingErrors] = useState<string[]>([]);

  // The `required` attributes on these inputs are inert: there is no wrapping
  // <form> and no submit event, so nothing ever enforced them and a customer
  // could reach "Place Order" with every field blank.
  const validateShipping = () => {
    const missing: string[] = [];
    const required: [keyof typeof formData, string][] = [
      ['email', 'email'], ['phone', 'phone'], ['firstName', 'first name'],
      ['lastName', 'last name'], ['address1', 'address'], ['city', 'city'],
      ['postcode', 'postal code'], ['state', 'province'],
    ];
    for (const [key, label] of required) {
      if (!String(formData[key] ?? '').trim()) missing.push(label);
    }
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      missing.push('a valid email');
    }
    return missing;
  };

  const handleContinueToPayment = () => {
    const missing = validateShipping();
    setShippingErrors(missing);
    if (missing.length === 0) setStep('payment');
  };

  const handleSubmit = async () => {
    setIsProcessing(true);
    setCheckoutError('');

    const shipping: ShippingAddress = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      address_1: formData.address1,
      address_2: formData.address2,
      city: formData.city,
      state: formData.state,
      postcode: formData.postcode,
      country: formData.country
    };

    const result = await checkout({
      billing: { ...shipping, email: formData.email, phone: formData.phone },
      shipping,
      payment_method: formData.paymentMethod,
      customer_note: formData.customerNote
    });

    setIsProcessing(false);

    if (result.success) {
      setOrderNumber(result.orderNumber || null);
      setOrderPaymentMethod(formData.paymentMethod);
      setOrderComplete(true);
      onOrderComplete?.();
    } else {
      setCheckoutError(result.error || 'Something went wrong. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div role="dialog" aria-modal="true" aria-label="Checkout" className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" style={{ background: '#F7F5EE' }}>
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 bg-white border-b" style={{ borderColor: '#e8e2d9' }}>
          <h2 className="font-serif text-2xl font-bold" style={{ color: '#1A2E12' }}>
            {orderComplete ? 'Order Confirmed!' : 'Checkout'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#F7F5EE] transition-colors"
          >
            <X className="w-6 h-6" style={{ color: '#6B7B6E' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {orderComplete ? (
            <div className="py-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#BDD630' }}>
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-1" style={{ color: '#1A2E12' }}>Order {orderNumber} confirmed!</h3>
                {/* Deliberately does NOT claim a confirmation email was sent: no
                    SMTP credentials are configured, so none is. Once mail is
                    wired up this can go back to promising an email. */}
                <p className="text-sm" style={{ color: '#6B7B6E' }}>
                  Please save your order number — we'll be in touch on {formData.email}.
                </p>
              </div>

              {/* EFT bank details */}
              {orderPaymentMethod === 'bacs' && (
                <div className="rounded-xl p-5 mb-5 border-2" style={{ borderColor: '#BDD630', background: '#f9fef0' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5" style={{ color: '#567D38' }} />
                    <span className="font-semibold" style={{ color: '#567D38' }}>Complete your EFT payment</span>
                  </div>
                  <div className="space-y-1 text-sm" style={{ color: '#1A2E12' }}>
                    <div className="flex justify-between"><span style={{ color: '#6B7B6E' }}>Bank</span><span className="font-medium">Standard Bank</span></div>
                    <div className="flex justify-between"><span style={{ color: '#6B7B6E' }}>Account Name</span><span className="font-medium">Marple &amp; Co</span></div>
                    <div className="flex justify-between"><span style={{ color: '#6B7B6E' }}>Branch Code</span><span className="font-medium">051001</span></div>
                    <div className="flex justify-between"><span style={{ color: '#6B7B6E' }}>Reference</span><span className="font-bold" style={{ color: '#567D38' }}>{orderNumber}</span></div>
                  </div>
                  <p className="text-xs mt-3" style={{ color: '#6B7B6E' }}>
                    Your order will be processed once payment reflects. Use your order number as the payment reference.
                  </p>
                </div>
              )}

              {/* COD confirmation */}
              {orderPaymentMethod === 'cod' && (
                <div className="rounded-xl p-5 mb-5 border-2" style={{ borderColor: '#BDD630', background: '#f9fef0' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-5 h-5" style={{ color: '#567D38' }} />
                    <span className="font-semibold" style={{ color: '#567D38' }}>Cash on Delivery</span>
                  </div>
                  <p className="text-sm" style={{ color: '#6B7B6E' }}>
                    Please have the exact amount ready for our courier. Payment is collected upon delivery.
                  </p>
                </div>
              )}

              <button onClick={onClose} className="w-full py-3 rounded-full font-semibold text-white" style={{ background: '#567D38' }}>
                Continue Shopping
              </button>
            </div>
          ) : (
            <>
              {/* Order Summary */}
              <div className="mb-6 p-4 rounded-xl bg-white">
                <h3 className="font-semibold mb-3" style={{ color: '#1A2E12' }}>Order Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: '#6B7B6E' }}>Subtotal</span>
                    <span style={{ color: '#1A2E12' }}>R{cartTotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between">
                      <span style={{ color: '#6B7B6E' }}>Discount {appliedCoupon && `(${appliedCoupon})`}</span>
                      <span style={{ color: '#567D38' }}>-R{discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span style={{ color: '#6B7B6E' }}>Shipping</span>
                    <span style={{ color: shipping === 0 ? '#567D38' : '#1A2E12' }}>
                      {shipping === 0 ? 'FREE' : `R${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="pt-2 border-t flex justify-between font-semibold" style={{ borderColor: '#e8e2d9' }}>
                    <span style={{ color: '#1A2E12' }}>Total</span>
                    <span style={{ color: '#567D38' }}>R{orderTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Form */}
              {step === 'shipping' && (
                <div className="space-y-4">
                  <h3 className="font-semibold" style={{ color: '#1A2E12' }}>Contact Information</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="col-span-2 px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <input
                      type="tel"
                      placeholder="Phone *"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="col-span-2 px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                  </div>

                  <h3 className="font-semibold pt-4" style={{ color: '#1A2E12' }}>Shipping Address</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Last Name *"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Address *"
                      value={formData.address1}
                      onChange={(e) => setFormData({ ...formData, address1: e.target.value })}
                      className="col-span-2 px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Apartment, suite, etc. (optional)"
                      value={formData.address2}
                      onChange={(e) => setFormData({ ...formData, address2: e.target.value })}
                      className="col-span-2 px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                    />
                    <input
                      type="text"
                      placeholder="City *"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Postal Code *"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      className="px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    />
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="col-span-2 px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                      style={{ borderColor: '#e8e2d9', background: 'white' }}
                      required
                    >
                      <option value="">Select Province *</option>
                      <option value="GP">Gauteng</option>
                      <option value="WC">Western Cape</option>
                      <option value="KZN">KwaZulu-Natal</option>
                      <option value="EC">Eastern Cape</option>
                      <option value="LP">Limpopo</option>
                      <option value="MP">Mpumalanga</option>
                      <option value="NW">North West</option>
                      <option value="FS">Free State</option>
                      <option value="NC">Northern Cape</option>
                    </select>
                  </div>

                  <textarea
                    placeholder="Order notes (optional)"
                    value={formData.customerNote}
                    onChange={(e) => setFormData({ ...formData, customerNote: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:border-[#BDD630]"
                    style={{ borderColor: '#e8e2d9', background: 'white' }}
                  />

                  {shippingErrors.length > 0 && (
                    <div
                      className="rounded-lg px-4 py-3 text-sm"
                      style={{ background: '#FDECE8', color: '#B4432A' }}
                      role="alert"
                    >
                      Please complete: {shippingErrors.join(', ')}.
                    </div>
                  )}

                  <button
                    onClick={handleContinueToPayment}
                    className="w-full py-4 rounded-full font-semibold text-white transition-all hover:scale-[1.02] mt-4"
                    style={{ background: '#567D38' }}
                  >
                    Continue to Payment
                  </button>
                </div>
              )}

              {/* Payment Form */}
              {step === 'payment' && (
                <div className="space-y-4">
                  <h3 className="font-semibold" style={{ color: '#1A2E12' }}>Payment Method</h3>

                  <div className="space-y-3">
                    {/* EFT / Bank Transfer */}
                    <label className={cn('flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all', formData.paymentMethod === 'bacs' ? 'border-[#BDD630] bg-[#BDD630]/5' : 'border-transparent bg-white')}>
                      <input type="radio" name="payment" value="bacs" checked={formData.paymentMethod === 'bacs'} onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })} className="w-5 h-5 accent-[#567D38]" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-5 h-5" style={{ color: '#567D38' }} />
                          <span className="font-semibold" style={{ color: '#1A2E12' }}>EFT / Bank Transfer</span>
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#6B7B6E' }}>We'll show banking details with your order confirmation</p>
                      </div>
                    </label>

                    {/* Cash on Delivery */}
                    <label className={cn('flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all', formData.paymentMethod === 'cod' ? 'border-[#BDD630] bg-[#BDD630]/5' : 'border-transparent bg-white')}>
                      <input type="radio" name="payment" value="cod" checked={formData.paymentMethod === 'cod'} onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })} className="w-5 h-5 accent-[#567D38]" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Truck className="w-5 h-5" style={{ color: '#567D38' }} />
                          <span className="font-semibold" style={{ color: '#1A2E12' }}>Cash on Delivery</span>
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#6B7B6E' }}>Pay cash when your order arrives</p>
                      </div>
                    </label>

                    {/* PayFast — coming soon */}
                    <div className="flex items-center gap-4 p-4 rounded-xl border-2 opacity-50 cursor-not-allowed" style={{ borderColor: '#e8e2d9', background: 'white' }}>
                      <div className="w-5 h-5 rounded-full border-2 flex-shrink-0" style={{ borderColor: '#e8e2d9' }} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CreditCard className="w-5 h-5" style={{ color: '#6B7B6E' }} />
                          <span className="font-semibold" style={{ color: '#6B7B6E' }}>PayFast (Card / Instant EFT)</span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#F7F5EE', color: '#6B7B6E' }}>Coming soon</span>
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#6B7B6E' }}>Card payments — currently being configured</p>
                      </div>
                    </div>

                    {/* PayFlex */}
                    <label className={cn('flex items-center gap-4 p-4 rounded-xl border-2 cursor-not-allowed opacity-50 transition-all', 'border-transparent bg-white')}>
                      <input type="radio" name="payment" value="payflex" checked={false} disabled readOnly className="w-5 h-5 accent-[#567D38]" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <CreditCard className="w-5 h-5" style={{ color: '#567D38' }} />
                          <span className="font-semibold" style={{ color: '#1A2E12' }}>PayFlex (Buy now, pay later)</span>
                        </div>
                        <p className="text-sm mt-1" style={{ color: '#6B7B6E' }}>
                          Coming soon — please use EFT or cash on delivery for now.
                        </p>
                      </div>
                    </label>
                  </div>

                  {checkoutError && (
                    <div className="p-3 rounded-lg text-sm" style={{ background: '#fef2f2', color: '#c0392b', border: '1px solid #fecaca' }}>
                      {checkoutError}
                    </div>
                  )}

                  <div className="flex items-center gap-3 p-4 rounded-xl bg-white">
                    <Shield className="w-5 h-5 flex-shrink-0" style={{ color: '#567D38' }} />
                    <p className="text-sm" style={{ color: '#6B7B6E' }}>Your order is processed securely. We never store card details.</p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button onClick={() => setStep('shipping')} className="flex-1 py-4 rounded-full font-semibold border-2" style={{ borderColor: '#567D38', color: '#567D38' }}>
                      Back
                    </button>
                    <button onClick={handleSubmit} disabled={isProcessing} className="flex-1 py-4 rounded-full font-semibold text-white disabled:opacity-50" style={{ background: '#567D38' }}>
                      {isProcessing ? 'Placing order…' : `Place Order — R${orderTotal.toFixed(2)}`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckoutModal;
